import React, { useEffect, useRef } from 'react';
import { LanguageThemeSelector } from '../common';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import './LandingPage.css';

/**
 * Landing page: embeds the Tilda page. Passes language to iframe for translation and image swap.
 * Iframe sends LANDING_SET_LANGUAGE on menu language click to sync app language.
 */
const LandingPage = () => {
  const { language, setLanguage } = useLanguage();
  const iframeRef = useRef(null);
  const visitStartedAtRef = useRef(null);
  const visitSessionIdRef = useRef(null);
  const languageRef = useRef(language);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    visitStartedAtRef.current = new Date();
    visitSessionIdRef.current = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    return () => {
      if (!visitStartedAtRef.current) return;
      const durationSeconds = Math.max(
        0,
        Math.round((Date.now() - visitStartedAtRef.current.getTime()) / 1000)
      );
      api.trackPageVisit(
        'landing',
        visitSessionIdRef.current,
        durationSeconds,
        visitStartedAtRef.current.toISOString(),
        { language: languageRef.current }
      ).catch(() => {});
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'LANDING_SET_LANGUAGE' && typeof event.data.lang === 'string') {
        setLanguage(event.data.lang);
      }
      // Navigate parent to auth/root without reload when user clicks "Вход"/"Регистрация" in iframe
      if (event.data?.type === 'LANDING_NAVIGATE' && typeof event.data.path === 'string') {
        window.history.pushState({}, '', event.data.path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setLanguage]);

  // Tell iframe the current language so it can translate and swap 6-step images
  useEffect(() => {
    const frame = iframeRef.current?.contentWindow;
    if (frame) {
      frame.postMessage({ type: 'LANDING_APPLY_LANG', lang: language }, '*');
    }
  }, [language]);

  // Fixed URL: language is applied via postMessage only, so switching language does not reload the iframe (smooth text change)
  const iframeSrc = `/landing-iframe/index.html?v=5`;

  const handleIframeLoad = () => {
    const frame = iframeRef.current?.contentWindow;
    if (frame) {
      frame.postMessage({ type: 'LANDING_APPLY_LANG', lang: language }, '*');
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-page__controls">
        <LanguageThemeSelector />
      </div>
      <iframe
        ref={iframeRef}
        title="Landing"
        src={iframeSrc}
        className="landing-page__iframe"
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default LandingPage;
export { LandingPage as LandingPageWrapper };
